import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Box,
  Typography,
  Alert,
  TextField,
  CircularProgress,
  Autocomplete,
  Checkbox,
  InputAdornment,
  Tooltip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import InventoryIcon from '@mui/icons-material/Inventory';
import { SelectedManifest } from '../utils/types';
import { SSCCProps } from '../../../datastore/ssccs';
import { VirtualizedListbox } from '../components/VirtualizedListbox';

interface AddSSCCToManifestDialogProps {
  open: boolean;
  selectedManifest: SelectedManifest | null;
  availableSSCCs: SSCCProps[];
  selectedSSCCId: string;
  ssccWeight: string;
  withReceiveManifest: boolean;
  isProcessing: boolean;
  onClose: () => void;
  onSSCCIdChange: (ssccId: string) => void;
  onWeightChange: (weight: string) => void;
  onReceiveManifestChange: (value: boolean) => void;
  onConfirm: (reason: string) => void;
  onAlert: (message: string, severity: 'error' | 'success' | 'info' | 'warning') => void;
}

export default function AddSSCCToManifestDialog({
  open,
  selectedManifest,
  availableSSCCs,
  selectedSSCCId,
  ssccWeight,
  withReceiveManifest,
  isProcessing,
  onClose,
  onSSCCIdChange,
  onWeightChange,
  onReceiveManifestChange,
  onConfirm,
  onAlert
}: AddSSCCToManifestDialogProps) {
  const [reasonDraft, setReasonDraft] = useState('');
  const [confirmReceiveManifestOpen, setConfirmReceiveManifestOpen] = useState(false);

  useEffect(() => {
    if (open) {
      setReasonDraft('');
      setConfirmReceiveManifestOpen(false);
    }
  }, [open]);

  const handleAddClick = () => {
    if (withReceiveManifest) {
      setConfirmReceiveManifestOpen(true);
      return;
    }

    onConfirm(reasonDraft);
  };

  const handleConfirmReceiveManifest = () => {
    setConfirmReceiveManifestOpen(false);
    onConfirm(reasonDraft);
  };

  const selectedSSCC = availableSSCCs.find((s) => s.id === selectedSSCCId);
  const cancelDisabledHint = isProcessing ? 'Please wait until the current action finishes.' : '';
  const confirmDisabledHint = isProcessing
    ? 'Add SSCC is in progress.'
    : !selectedSSCCId
      ? 'Select an SSCC to continue.'
      : !reasonDraft.trim()
        ? 'Reason for adding is required.'
        : '';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 12px 40px rgba(0,0,0,0.15)'
        }
      }}>
      <DialogTitle
        sx={{
          background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
          color: '#fff',
          px: 3,
          py: 3
        }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              backgroundColor: 'rgba(255, 255, 255, 0.15)',
              display: 'flex',
              backdropFilter: 'blur(10px)'
            }}>
            <AddIcon sx={{ fontSize: 24, color: '#fff' }} />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={600} sx={{ color: '#fff' }}>
              Add SSCC to Manifest
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.85)', mt: 0.5 }}>
              Add an SSCC that was received without a manifest
            </Typography>
          </Box>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ px: 3, py: 3, '& > :first-of-type': { mt: 1 } }}>
        <Stack spacing={3}>
          <Alert
            severity="info"
            icon={<InfoOutlinedIcon />}
            sx={{
              borderRadius: 2,
              border: '1px solid rgba(2, 136, 209, 0.2)',
              backgroundColor: 'rgba(2, 136, 209, 0.04)',
              fontSize: '0.875rem',
              '& .MuiAlert-icon': {
                color: '#0288d1'
              }
            }}>
            Adding this SSCC will update its status to{' '}
            <Box component="span" sx={{ fontWeight: 700, color: '#0277bd' }}>
              '{selectedManifest?.ssccStatus}'
            </Box>{' '}
            and all ASN statuses to{' '}
            <Box component="span" sx={{ fontWeight: 700, color: '#0277bd' }}>
              '{selectedManifest?.asnStatus}'
            </Box>
            {'. '}
            {selectedManifest?.manifest?.status === 'Completed' && (
              <>You can choose whether or not to receive the manifest.</>
            )}
          </Alert>

          <Box
            sx={{
              p: 2.5,
              borderRadius: 2,
              backgroundColor: 'rgba(25, 118, 210, 0.04)',
              border: '2px solid rgba(25, 118, 210, 0.15)'
            }}>
            <Typography
              variant="caption"
              sx={{
                textTransform: 'uppercase',
                letterSpacing: 0.8,
                fontSize: '0.7rem',
                fontWeight: 700,
                color: '#1976d2'
              }}>
              Manifest ID
            </Typography>
            <Typography variant="h6" fontWeight={600} sx={{ mt: 1, color: '#1565c0' }}>
              {selectedManifest?.manifest?.id}
            </Typography>
          </Box>

          <Box>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                mb: 1.5,
                fontSize: '0.875rem'
              }}>
              Select SSCC{' '}
              <Box component="span" sx={{ color: 'error.main' }}>
                *
              </Box>
            </Typography>
            <Typography
              variant="caption"
              sx={{ display: 'block', color: 'text.secondary', mb: 1, fontSize: '0.75rem' }}>
              Eligible SSCCs are those with status Open or Staged that are not already in this manifest.
            </Typography>
            <Autocomplete
              options={availableSSCCs}
              getOptionLabel={(option) =>
                `${option.id}${option.nickname ? ` (${option.nickname})` : ''} - ${option.status || 'Unknown'}`
              }
              value={availableSSCCs.find((s) => s.id === selectedSSCCId) || null}
              onChange={(_, newValue) => onSSCCIdChange(newValue?.id || '')}
              ListboxComponent={VirtualizedListbox as React.ComponentType<React.HTMLAttributes<HTMLElement>>}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search for SSCC..."
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      fontSize: '0.875rem'
                    }
                  }}
                />
              )}
              renderOption={(props, option) => {
                const { key, ...optionProps } = props;
                return (
                  <li key={key} {...optionProps}>
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: '100%', py: 1 }}>
                      <Box
                        sx={{
                          p: 1,
                          borderRadius: 1.5,
                          backgroundColor: 'rgba(25, 118, 210, 0.08)',
                          display: 'flex'
                        }}>
                        <InventoryIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                      </Box>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="body2" fontWeight={600}>
                          {option.id}
                          {option.nickname ? ` (${option.nickname})` : ''}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.status || 'Unknown'}
                        </Typography>
                      </Box>
                    </Stack>
                  </li>
                );
              }}
            />
          </Box>

          <Box>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                mb: 1.5,
                fontSize: '0.875rem'
              }}>
              Reason for Adding{' '}
              <Box component="span" sx={{ color: 'error.main' }}>
                *
              </Box>
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              required
              value={reasonDraft}
              onChange={(e) => setReasonDraft(e.target.value)}
              placeholder="Explain why this SSCC is being added to the manifest (e.g., received without manifest, system error, etc.)..."
              helperText="This reason will be logged in the audit trail"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  fontSize: '0.875rem'
                }
              }}
            />
          </Box>

          {selectedSSCCId && !selectedSSCC?.weight ? (
            <Box>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                  mb: 1.5,
                  fontSize: '0.875rem'
                }}>
                Weight
              </Typography>
              <TextField
                size="small"
                placeholder="Enter Weight"
                type="number"
                value={ssccWeight}
                onChange={(e) => {
                  const value = Number(e.target.value);

                  if (value <= 500000) {
                    onWeightChange(value.toString());
                  } else {
                    onAlert('Weight (kg) cannot exceed 500,000', 'error');
                  }
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" sx={{ fontSize: '0.75rem', fontWeight: 600 }}>
                      KG
                    </InputAdornment>
                  ),
                  sx: {
                    fontSize: '0.875rem',
                    borderRadius: 2
                  }
                }}
                sx={{
                  maxWidth: 200
                }}
              />
            </Box>
          ) : null}
          {selectedManifest?.manifest?.status === 'Completed' ? (
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                backgroundColor: 'rgba(0, 0, 0, 0.02)',
                border: '1px solid rgba(0, 0, 0, 0.08)'
              }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Checkbox
                  size="small"
                  checked={withReceiveManifest}
                  onChange={(e) => onReceiveManifestChange(e.target.checked)}
                />
                <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.875rem' }}>
                  Receive Manifest
                </Typography>
              </Stack>
            </Box>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          py: 2.5,
          backgroundColor: 'rgba(0, 0, 0, 0.02)',
          borderTop: '1px solid rgba(0, 0, 0, 0.08)',
          gap: 1.5
        }}>
        <Tooltip title={cancelDisabledHint} disableHoverListener={!cancelDisabledHint} arrow>
          <span>
            <Button
              onClick={onClose}
              disabled={isProcessing}
              startIcon={isProcessing ? <CircularProgress size={16} color="inherit" /> : undefined}
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                color: 'text.secondary',
                fontSize: '0.875rem',
                px: 2.5,
                py: 1
              }}>
              Cancel
            </Button>
          </span>
        </Tooltip>
        <Tooltip title={confirmDisabledHint} disableHoverListener={!confirmDisabledHint} arrow>
          <span>
            <Button
              variant="contained"
              onClick={handleAddClick}
              disabled={!selectedSSCCId || !reasonDraft.trim() || isProcessing}
              startIcon={
                isProcessing ? <CircularProgress size={16} color="inherit" /> : <AddIcon sx={{ fontSize: 18 }} />
              }
              sx={{
                px: 3,
                py: 1,
                fontWeight: 600,
                fontSize: '0.875rem',
                textTransform: 'none',
                backgroundColor: '#1976d2',
                '&:hover': { backgroundColor: '#1565c0' },
                boxShadow: 'none',
                borderRadius: 1.5
              }}>
              {isProcessing ? 'Adding...' : 'Add SSCC'}
            </Button>
          </span>
        </Tooltip>
      </DialogActions>

      <Dialog
        open={confirmReceiveManifestOpen}
        onClose={() => setConfirmReceiveManifestOpen(false)}
        maxWidth="xs"
        fullWidth>
        <DialogTitle sx={{ pb: 1 }}>Confirm Receive Manifest</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mt: 1 }}>
            This action will do Receive Manifest. Are you sure you want to continue?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmReceiveManifestOpen(false)} disabled={isProcessing}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleConfirmReceiveManifest} disabled={isProcessing}>
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
